<template>
  <div
    class="markdown-body"
    :class="{ 'markdown-compact': density === 'compact' }"
    v-html="html"
    @click="onClick"
  />
</template>

<script setup lang="ts">
import { displayProse } from '@/utils/typography'
import { documentDisplayName } from '@/utils/documentDisplayName'
import { computed } from 'vue'
import type { PropType } from 'vue'
import { useRouter } from 'vue-router'
import { marked } from 'marked'
import type { Token } from 'marked'
import { sanitizeMarkdownHtml } from '@/utils/sanitizeMarkdownHtml'

/** A code span whose text carries a file extension is a DOCUMENT NAME
 * (IR-116 #9) — restyled to match the shared `DocRef` chip. Clause-code spans
 * (`# 6.4.1`) and requirement uids never match. */
const DOC_NAME_RE = /\.(pdf|docx?|xlsx?|md|csv)\b/i

function buildRenderer(docRefs: boolean, isInAppRoute: (href: string | undefined) => boolean) {
  const renderer = new marked.Renderer()
  const defaultLinkRenderer = renderer.link.bind(renderer)
  renderer.link = function (token) {
    const html = defaultLinkRenderer(token)
    // A link to somewhere else opens in a new tab, so the reader does not lose
    // this page. A link back into THIS app must not: a new tab boots the SPA
    // from scratch, and the chat store does not rehydrate a conversation — the
    // user watches their conversation disappear to read a document they were
    // just told about (MYAC-1162, Aymeric).
    //
    // The question asked here is the SAME one the click asks — "is this a page
    // this router can open?" — and it must stay that way. Asking a looser one
    // here (merely "same origin") drops the new tab from a link the click then
    // declines to intercept, such as `/assets/guide.pdf`: the browser navigates
    // in place and the conversation is lost, which is the bug, not the fix.
    if (isInAppRoute(token.href)) return html
    return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ')
  }
  if (docRefs) {
    const defaultCodespan = renderer.codespan.bind(renderer)
    renderer.codespan = function (token) {
      if (!DOC_NAME_RE.test(token.text)) return defaultCodespan(token)
      // MYAC-1343 — the engine writes the ingestion's converted name into its
      // prose (`x.docx.pdf`, `y.xlsx.md`); the chip shows the name the client
      // knows. Label only: the span is not a link and nothing resolves by it.
      const html = defaultCodespan({ ...token, text: documentDisplayName(token.text) })
      return html.replace(
        '<code>',
        '<code class="md-doc-ref"><i class="pi pi-file" aria-hidden="true"></i>',
      )
    }
  }
  return renderer
}

const props = defineProps({
  content: String,
  /**
   * MYAC-1162 — first refusal on a link into this app. Return true to say "I
   * handled it" and no navigation happens; return false (or leave it unset) and
   * the link is followed with `router.push`, in-app.
   *
   * The chat uses it to open a document BESIDE the conversation instead of
   * navigating away from it.
   */
  internalLinkHandler: {
    type: Function as PropType<(path: string) => boolean>,
    default: undefined,
  },
  /** Opt-in (IR-116 #9): document-name code spans get the DocRef chip look.
   * Only the assessment surfaces (verdict cards, summary) turn this on — chat
   * and run summaries keep the plain code render. */
  docRefs: Boolean,
  /** Opt-in (MYAC-1151): `compact` tightens the vertical rhythm for the chat
   * panel, which renders this stylesheet at ~420px. See the `.markdown-compact`
   * block at the end of the styles for what it changes and why.
   *
   * Opt-in rather than a change to `.markdown-body` because 30-odd other
   * surfaces render the same class — assessment summaries, findings, tasks, run
   * summaries, the pricing page — and none of them asked for this. */
  density: {
    type: String as PropType<'default' | 'compact'>,
    default: 'default',
  },
  /** Opt-out (MYAC-1302): by default every em dash in the rendered TEXT reads
   * as a plain hyphen, because model output carries them and they read as
   * machine-written. Code spans, code blocks and link targets are never
   * touched. Set `verbatim` where the text must stay exactly as written: the
   * user's own words, quotations from a client's documents, legal texts.
   * Opt-out rather than opt-in so a new AI surface cannot forget it;
   * `noEmDashInCode.test.ts` pins the verbatim sites. */
  verbatim: Boolean,
})

/**
 * The in-app path this href points at, or null when it points elsewhere.
 *
 * Relative hrefs and absolute ones on this very origin are in-app; everything
 * else — another site, `mailto:`, a protocol-relative `//host/x` — is not.
 */
function internalPath(href: string | undefined | null): string | null {
  if (!href || href.startsWith('//')) return null
  if (href.startsWith('/')) return href
  try {
    const url = new URL(href, window.location.origin)
    if (url.origin !== window.location.origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

const router = useRouter()

/**
 * Can this router open that href as a page? Both the renderer and the click
 * handler go through here, so they cannot disagree.
 *
 * No router (a component mounted outside one) means no: the link keeps the new
 * tab it would have had before any of this existed.
 */
function isInAppRoute(href: string | undefined | null): boolean {
  const path = internalPath(href)
  if (path === null || !router) return false
  try {
    return router.resolve(path).matched.length > 0
  } catch {
    return false
  }
}

// Per instance, because it closes over this instance's router. Cheap beside the
// markdown parse that follows it.
const renderer = computed(() => buildRenderer(false, isInAppRoute))
const docRefRenderer = computed(() => buildRenderer(true, isInAppRoute))

/**
 * Follow a link into this app without reloading it.
 *
 * Left untouched: modified clicks (the user asking for a new tab deliberately),
 * links to other sites, and same-origin paths this router has no route for —
 * a file under `/assets`, say, which `router.push` would turn into a 404.
 */
function onClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]')
  if (!anchor) return

  const href = anchor.getAttribute('href')
  if (!isInAppRoute(href)) return
  const path = internalPath(href) as string

  event.preventDefault()
  if (props.internalLinkHandler?.(path)) return
  router.push(path)
}

/** Text tokens only: `code` / `codespan` tokens and link hrefs keep every
 * character. Strong, em, link labels and table cells are walked into, so their
 * text children are covered.
 *
 * MYAC-1343 — the same walk drops the ingestion's conversion suffix from a file
 * name written INSIDE a sentence, which is how the workers name documents in
 * prose. A codespan is not a text token, so the chip path below handles that
 * case separately; `verbatim` skips both. */
function normalizeProseTokens(token: Token): void {
  if (token.type === 'text') token.text = displayProse(token.text)
}

// Sanitized because marked passes raw HTML through and this lands in v-html: the
// rules (no script, no resource load, safe links) live in `sanitizeMarkdownHtml`.
const html = computed(() =>
  sanitizeMarkdownHtml(
    marked(props.content ?? '', {
      renderer: props.docRefs ? docRefRenderer.value : renderer.value,
      walkTokens: props.verbatim ? undefined : normalizeProseTokens,
    }) as string,
  ),
)
</script>

<style>
.markdown-body {
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  /* MYAC-1494: the app's typeface (Poppins), not GitHub's system stack. */
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
}

.markdown-body .octicon {
  display: inline-block;
  fill: currentColor;
  vertical-align: text-bottom;
}

.markdown-body h1:hover .anchor .octicon-link:before,
.markdown-body h2:hover .anchor .octicon-link:before,
.markdown-body h3:hover .anchor .octicon-link:before,
.markdown-body h4:hover .anchor .octicon-link:before,
.markdown-body h5:hover .anchor .octicon-link:before,
.markdown-body h6:hover .anchor .octicon-link:before {
  width: 16px;
  height: 16px;
  content: ' ';
  display: inline-block;
  background-color: currentColor;
  -webkit-mask-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' version='1.1' aria-hidden='true'><path fill-rule='evenodd' d='M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z'></path></svg>");
  mask-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' version='1.1' aria-hidden='true'><path fill-rule='evenodd' d='M7.775 3.275a.75.75 0 001.06 1.06l1.25-1.25a2 2 0 112.83 2.83l-2.5 2.5a2 2 0 01-2.83 0 .75.75 0 00-1.06 1.06 3.5 3.5 0 004.95 0l2.5-2.5a3.5 3.5 0 00-4.95-4.95l-1.25 1.25zm-4.69 9.64a2 2 0 010-2.83l2.5-2.5a2 2 0 012.83 0 .75.75 0 001.06-1.06 3.5 3.5 0 00-4.95 0l-2.5 2.5a3.5 3.5 0 004.95 4.95l1.25-1.25a.75.75 0 00-1.06-1.06l-1.25 1.25a2 2 0 01-2.83 0z'></path></svg>");
}

.markdown-body details,
.markdown-body figcaption,
.markdown-body figure {
  display: block;
}

.markdown-body summary {
  display: list-item;
}

.markdown-body [hidden] {
  display: none !important;
}

.markdown-body a {
  background-color: transparent;
  color: #0969da;
  text-decoration: none;
}

.markdown-body abbr[title] {
  border-bottom: none;
  -webkit-text-decoration: underline dotted;
  text-decoration: underline dotted;
}

.markdown-body b,
.markdown-body strong {
  font-weight: 600;
}

.markdown-body dfn {
  font-style: italic;
}

.markdown-body h1 {
  margin: 0.67em 0;
  font-weight: 600;
  padding-bottom: 0.3em;
  font-size: 2em;
  border-bottom: 1px solid #d1d9e0b3;
}

.markdown-body mark {
  background-color: #fff8c5;
  color: #1f2328;
}

.markdown-body small {
  font-size: 90%;
}

.markdown-body sub,
.markdown-body sup {
  font-size: 75%;
  line-height: 0;
  position: relative;
  vertical-align: baseline;
}

.markdown-body sub {
  bottom: -0.25em;
}

.markdown-body sup {
  top: -0.5em;
}

.markdown-body img {
  border-style: none;
  max-width: 100%;
  box-sizing: content-box;
}

.markdown-body code,
.markdown-body kbd,
.markdown-body pre,
.markdown-body samp {
  font-family: monospace;
  font-size: 1em;
}

.markdown-body figure {
  margin: 1em 2.5rem;
}

.markdown-body hr {
  box-sizing: content-box;
  overflow: hidden;
  background: transparent;
  border-bottom: 1px solid #d1d9e0b3;
  height: 0.25em;
  padding: 0;
  margin: 1.5rem 0;
  background-color: #d1d9e0;
  border: 0;
}

.markdown-body input {
  font: inherit;
  margin: 0;
  overflow: visible;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.markdown-body [type='button'],
.markdown-body [type='reset'],
.markdown-body [type='submit'] {
  -webkit-appearance: button;
  appearance: button;
}

.markdown-body [type='checkbox'],
.markdown-body [type='radio'] {
  box-sizing: border-box;
  padding: 0;
}

.markdown-body [type='number']::-webkit-inner-spin-button,
.markdown-body [type='number']::-webkit-outer-spin-button {
  height: auto;
}

.markdown-body [type='search']::-webkit-search-cancel-button,
.markdown-body [type='search']::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.markdown-body ::-webkit-input-placeholder {
  color: inherit;
  opacity: 0.54;
}

.markdown-body ::-webkit-file-upload-button {
  -webkit-appearance: button;
  appearance: button;
  font: inherit;
}

.markdown-body a:hover {
  text-decoration: underline;
}

.markdown-body ::placeholder {
  color: #59636e;
  opacity: 1;
}

.markdown-body hr::before {
  display: table;
  content: '';
}

.markdown-body hr::after {
  display: table;
  clear: both;
  content: '';
}

.markdown-body table {
  border-spacing: 0;
  border-collapse: collapse;
  display: block;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  font-variant: tabular-nums;
}

.markdown-body td,
.markdown-body th {
  padding: 0;
}

.markdown-body details summary {
  cursor: pointer;
}

.markdown-body a:focus,
.markdown-body [role='button']:focus,
.markdown-body input[type='radio']:focus,
.markdown-body input[type='checkbox']:focus {
  outline: 2px solid #0969da;
  outline-offset: -2px;
  box-shadow: none;
}

.markdown-body a:focus:not(:focus-visible),
.markdown-body [role='button']:focus:not(:focus-visible),
.markdown-body input[type='radio']:focus:not(:focus-visible),
.markdown-body input[type='checkbox']:focus:not(:focus-visible) {
  outline: solid 1px transparent;
}

.markdown-body a:focus-visible,
.markdown-body [role='button']:focus-visible,
.markdown-body input[type='radio']:focus-visible,
.markdown-body input[type='checkbox']:focus-visible {
  outline: 2px solid #0969da;
  outline-offset: -2px;
  box-shadow: none;
}

.markdown-body a:not([class]):focus,
.markdown-body a:not([class]):focus-visible,
.markdown-body input[type='radio']:focus,
.markdown-body input[type='radio']:focus-visible,
.markdown-body input[type='checkbox']:focus,
.markdown-body input[type='checkbox']:focus-visible {
  outline-offset: 0;
}

.markdown-body kbd {
  display: inline-block;
  padding: 0.25rem;
  font:
    11px ui-monospace,
    SFMono-Regular,
    SF Mono,
    Menlo,
    Consolas,
    Liberation Mono,
    monospace;
  line-height: 10px;
  color: #1f2328;
  vertical-align: middle;
  background-color: #f6f8fa;
  border: solid 1px #d1d9e0b3;
  border-bottom-color: #d1d9e0b3;
  border-radius: 6px;
  box-shadow: inset 0 -1px 0 #d1d9e0b3;
}

.markdown-body h1,
.markdown-body h2,
.markdown-body h3,
.markdown-body h4,
.markdown-body h5,
.markdown-body h6 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-body h2 {
  font-weight: 600;
  padding-bottom: 0.3em;
  font-size: 1.5em;
  border-bottom: 1px solid #d1d9e0b3;
}

.markdown-body h3 {
  font-weight: 600;
  font-size: 1.25em;
}

.markdown-body h4 {
  font-weight: 600;
  font-size: 1em;
}

.markdown-body h5 {
  font-weight: 600;
  font-size: 0.875em;
}

.markdown-body h6 {
  font-weight: 600;
  font-size: 0.85em;
  color: #59636e;
}

.markdown-body p {
  margin-top: 0;
  margin-bottom: 10px;
}

.markdown-body blockquote {
  margin: 0;
  padding: 0 1em;
  color: #59636e;
  border-left: 0.25em solid #d1d9e0;
}

.markdown-body ul,
.markdown-body ol {
  margin-top: 0;
  margin-bottom: 0;
  padding-left: 2em;
}

/* ---------------------------------------------------------------------------
 * List markers, and why they are set on the LIST rather than on the item.
 *
 * Tailwind's preflight sets `list-style: none` on every `ol`/`ul`, so the
 * browser defaults this stylesheet was written against (it is
 * github-markdown-css) are gone and every marker has to be restored here.
 * `ol` never was, so a top-level `1. 2. 3.` rendered as bare indented lines
 * — app-wide, on every AI-authored finding, chat message and run summary.
 *
 * `disc` used to sit on `.markdown-body ul li`, and that is the subtler half.
 * The nesting ladder below (`ol ol` → lower-roman, depth 3 → lower-alpha)
 * targets the LIST and reaches the item by inheritance — which a value set
 * directly on the item beats regardless of order. So an `ol` inside a `ul` got
 * `disc` from the item rule instead of `lower-roman` from its own list, and
 * rendered as bullets.
 *
 * Set on the list, the ladder composes by specificity alone: `.markdown-body
 * ul` (0,1,1) loses to `.markdown-body ul ol` (0,1,2) loses to the depth-3
 * rules (0,1,3), and `.no-list` / `ol[type]` / `.task-list-item` all carry a
 * second class and still win. Nothing depends on declaration order.
 * ------------------------------------------------------------------------- */
.markdown-body ul {
  list-style-type: disc;
}

.markdown-body ol {
  list-style-type: decimal;
}

.markdown-body ul li {
  text-align: left;
}

.markdown-body ol ol,
.markdown-body ul ol {
  list-style-type: lower-roman;
}

.markdown-body ul ul ol,
.markdown-body ul ol ol,
.markdown-body ol ul ol,
.markdown-body ol ol ol {
  list-style-type: lower-alpha;
}

.markdown-body dd {
  margin-left: 0;
}

.markdown-body tt,
.markdown-body code,
.markdown-body samp {
  font-family:
    ui-monospace,
    SFMono-Regular,
    SF Mono,
    Menlo,
    Consolas,
    Liberation Mono,
    monospace;
  font-size: 12px;
}

.markdown-body pre {
  margin-top: 0;
  margin-bottom: 0;
  font-family:
    ui-monospace,
    SFMono-Regular,
    SF Mono,
    Menlo,
    Consolas,
    Liberation Mono,
    monospace;
  font-size: 12px;
  word-wrap: normal;
}

.markdown-body .octicon {
  display: inline-block;
  overflow: visible !important;
  vertical-align: text-bottom;
  fill: currentColor;
}

.markdown-body input::-webkit-outer-spin-button,
.markdown-body input::-webkit-inner-spin-button {
  margin: 0;
  appearance: none;
}

.markdown-body .mr-2 {
  margin-right: 0.5rem !important;
}

.markdown-body::before {
  display: table;
  content: '';
}

.markdown-body::after {
  display: table;
  clear: both;
  content: '';
}

.markdown-body > *:first-child {
  margin-top: 0 !important;
}

.markdown-body > *:last-child {
  margin-bottom: 0 !important;
}

.markdown-body a:not([href]) {
  color: inherit;
  text-decoration: none;
}

.markdown-body .absent {
  color: #d1242f;
}

.markdown-body .anchor {
  float: left;
  padding-right: 0.25rem;
  margin-left: -20px;
  line-height: 1;
}

.markdown-body .anchor:focus {
  outline: none;
}

.markdown-body p,
.markdown-body blockquote,
.markdown-body ul,
.markdown-body ol,
.markdown-body dl,
.markdown-body table,
.markdown-body pre,
.markdown-body details {
  margin-top: 0;
  margin-bottom: 1rem;
}

.markdown-body blockquote > :first-child {
  margin-top: 0;
}

.markdown-body blockquote > :last-child {
  margin-bottom: 0;
}

.markdown-body h1 .octicon-link,
.markdown-body h2 .octicon-link,
.markdown-body h3 .octicon-link,
.markdown-body h4 .octicon-link,
.markdown-body h5 .octicon-link,
.markdown-body h6 .octicon-link {
  color: #1f2328;
  vertical-align: middle;
  visibility: hidden;
}

.markdown-body h1:hover .anchor,
.markdown-body h2:hover .anchor,
.markdown-body h3:hover .anchor,
.markdown-body h4:hover .anchor,
.markdown-body h5:hover .anchor,
.markdown-body h6:hover .anchor {
  text-decoration: none;
}

.markdown-body h1:hover .anchor .octicon-link,
.markdown-body h2:hover .anchor .octicon-link,
.markdown-body h3:hover .anchor .octicon-link,
.markdown-body h4:hover .anchor .octicon-link,
.markdown-body h5:hover .anchor .octicon-link,
.markdown-body h6:hover .anchor .octicon-link {
  visibility: visible;
}

.markdown-body h1 tt,
.markdown-body h1 code,
.markdown-body h2 tt,
.markdown-body h2 code,
.markdown-body h3 tt,
.markdown-body h3 code,
.markdown-body h4 tt,
.markdown-body h4 code,
.markdown-body h5 tt,
.markdown-body h5 code,
.markdown-body h6 tt,
.markdown-body h6 code {
  padding: 0 0.2em;
  font-size: inherit;
}

.markdown-body summary h1,
.markdown-body summary h2,
.markdown-body summary h3,
.markdown-body summary h4,
.markdown-body summary h5,
.markdown-body summary h6 {
  display: inline-block;
}

.markdown-body summary h1 .anchor,
.markdown-body summary h2 .anchor,
.markdown-body summary h3 .anchor,
.markdown-body summary h4 .anchor,
.markdown-body summary h5 .anchor,
.markdown-body summary h6 .anchor {
  margin-left: -40px;
}

.markdown-body summary h1,
.markdown-body summary h2 {
  padding-bottom: 0;
  border-bottom: 0;
}

.markdown-body ul.no-list,
.markdown-body ol.no-list {
  padding: 0;
  list-style-type: none;
}

.markdown-body ol[type='a s'] {
  list-style-type: lower-alpha;
}

.markdown-body ol[type='A s'] {
  list-style-type: upper-alpha;
}

.markdown-body ol[type='i s'] {
  list-style-type: lower-roman;
}

.markdown-body ol[type='I s'] {
  list-style-type: upper-roman;
}

.markdown-body ol[type='1'] {
  list-style-type: decimal;
}

.markdown-body div > ol:not([type]) {
  list-style-type: decimal;
}

.markdown-body ul ul,
.markdown-body ul ol,
.markdown-body ol ol,
.markdown-body ol ul {
  margin-top: 0;
  margin-bottom: 0;
}

.markdown-body li > p {
  margin-top: 1rem;
}

.markdown-body li + li {
  margin-top: 0.25em;
}

.markdown-body dl {
  padding: 0;
}

.markdown-body dl dt {
  padding: 0;
  margin-top: 1rem;
  font-size: 1em;
  font-style: italic;
  font-weight: 600;
}

.markdown-body dl dd {
  padding: 0 1rem;
  margin-bottom: 1rem;
}

.markdown-body table th {
  font-weight: 600;
}

.markdown-body table th,
.markdown-body table td {
  padding: 6px 13px;
  border: 1px solid #d1d9e0;
}

.markdown-body table td > :last-child {
  margin-bottom: 0;
}

.markdown-body table tr {
  background-color: #ffffff;
  border-top: 1px solid #d1d9e0b3;
}

.markdown-body table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

.markdown-body table img {
  background-color: transparent;
}

.markdown-body img[align='right'] {
  padding-left: 20px;
}

.markdown-body img[align='left'] {
  padding-right: 20px;
}

.markdown-body .emoji {
  max-width: none;
  vertical-align: text-top;
  background-color: transparent;
}

.markdown-body span.frame {
  display: block;
  overflow: hidden;
}

.markdown-body span.frame > span {
  display: block;
  float: left;
  width: auto;
  padding: 7px;
  margin: 13px 0 0;
  overflow: hidden;
  border: 1px solid #d1d9e0;
}

.markdown-body span.frame span img {
  display: block;
  float: left;
}

.markdown-body span.frame span span {
  display: block;
  padding: 5px 0 0;
  clear: both;
  color: #1f2328;
}

.markdown-body span.align-center {
  display: block;
  overflow: hidden;
  clear: both;
}

.markdown-body span.align-center > span {
  display: block;
  margin: 13px auto 0;
  overflow: hidden;
  text-align: center;
}

.markdown-body span.align-center span img {
  margin: 0 auto;
  text-align: center;
}

.markdown-body span.align-right {
  display: block;
  overflow: hidden;
  clear: both;
}

.markdown-body span.align-right > span {
  display: block;
  margin: 13px 0 0;
  overflow: hidden;
  text-align: right;
}

.markdown-body span.align-right span img {
  margin: 0;
  text-align: right;
}

.markdown-body span.float-left {
  display: block;
  float: left;
  margin-right: 13px;
  overflow: hidden;
}

.markdown-body span.float-left span {
  margin: 13px 0 0;
}

.markdown-body span.float-right {
  display: block;
  float: right;
  margin-left: 13px;
  overflow: hidden;
}

.markdown-body span.float-right > span {
  display: block;
  margin: 13px auto 0;
  overflow: hidden;
  text-align: right;
}

.markdown-body code,
.markdown-body tt {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  white-space: break-spaces;
  background-color: #818b981f;
  border-radius: 6px;
}

.markdown-body code br,
.markdown-body tt br {
  display: none;
}

/* Document-name code spans (IR-116 #9) — the shared DocRef chip look, so a
 * filename in rendered markdown matches the structured surfaces' chip. Font
 * back to the body's (a doc name is a name, not code). */
.markdown-body code.md-doc-ref {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  padding: 0 0.25rem;
  border: 1px solid var(--p-surface-300);
  border-radius: 4px;
  background-color: var(--p-surface-100);
  color: var(--p-text-muted-color);
  font-family: inherit;
  font-size: 0.75rem;
}

.markdown-body code.md-doc-ref .pi {
  font-size: 0.625rem;
  flex-shrink: 0;
}

/* Dark variant — inert today (light-only app, `.mac-dark` never set), written
 * for the day the real dark theme ships, like the components' `dark:` classes. */
.mac-dark .markdown-body code.md-doc-ref {
  border-color: var(--p-surface-600);
  background-color: var(--p-surface-800);
}

.markdown-body del code {
  text-decoration: inherit;
}

.markdown-body samp {
  font-size: 85%;
}

.markdown-body pre code {
  font-size: 100%;
}

.markdown-body pre > code {
  padding: 0;
  margin: 0;
  word-break: normal;
  white-space: pre;
  background: transparent;
  border: 0;
}

.markdown-body .highlight {
  margin-bottom: 1rem;
}

.markdown-body .highlight pre {
  margin-bottom: 0;
  word-break: normal;
}

.markdown-body .highlight pre,
.markdown-body pre {
  padding: 1rem;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  color: #1f2328;
  background-color: #f6f8fa;
  border-radius: 6px;
}

.markdown-body pre code,
.markdown-body pre tt {
  display: inline;
  max-width: auto;
  padding: 0;
  margin: 0;
  overflow: visible;
  line-height: inherit;
  word-wrap: normal;
  background-color: transparent;
  border: 0;
}

.markdown-body .csv-data td,
.markdown-body .csv-data th {
  padding: 5px;
  overflow: hidden;
  font-size: 12px;
  line-height: 1;
  text-align: left;
  white-space: nowrap;
}

.markdown-body .csv-data .blob-num {
  padding: 10px 0.5rem 9px;
  text-align: right;
  background: #ffffff;
  border: 0;
}

.markdown-body .csv-data tr {
  border-top: 0;
}

.markdown-body .csv-data th {
  font-weight: 600;
  background: #f6f8fa;
  border-top: 0;
}

.markdown-body [data-footnote-ref]::before {
  content: '[';
}

.markdown-body [data-footnote-ref]::after {
  content: ']';
}

.markdown-body .footnotes {
  font-size: 12px;
  color: #59636e;
  border-top: 1px solid #d1d9e0;
}

.markdown-body .footnotes ol {
  padding-left: 1rem;
}

.markdown-body .footnotes ol ul {
  display: inline-block;
  padding-left: 1rem;
  margin-top: 1rem;
}

.markdown-body .footnotes li {
  position: relative;
}

.markdown-body .footnotes li:target::before {
  position: absolute;
  top: calc(0.5rem * -1);
  right: calc(0.5rem * -1);
  bottom: calc(0.5rem * -1);
  left: calc(1.5rem * -1);
  pointer-events: none;
  content: '';
  border: 2px solid #0969da;
  border-radius: 6px;
}

.markdown-body .footnotes li:target {
  color: #1f2328;
}

.markdown-body .footnotes .data-footnote-backref g-emoji {
  font-family: monospace;
}

.markdown-body body:has(:modal) {
  padding-right: var(--dialog-scrollgutter) !important;
}

.markdown-body .pl-c {
  color: #59636e;
}

.markdown-body .pl-c1,
.markdown-body .pl-s .pl-v {
  color: #0550ae;
}

.markdown-body .pl-e,
.markdown-body .pl-en {
  color: #6639ba;
}

.markdown-body .pl-smi,
.markdown-body .pl-s .pl-s1 {
  color: #1f2328;
}

.markdown-body .pl-ent {
  color: #0550ae;
}

.markdown-body .pl-k {
  color: #cf222e;
}

.markdown-body .pl-s,
.markdown-body .pl-pds,
.markdown-body .pl-s .pl-pse .pl-s1,
.markdown-body .pl-sr,
.markdown-body .pl-sr .pl-cce,
.markdown-body .pl-sr .pl-sre,
.markdown-body .pl-sr .pl-sra {
  color: #0a3069;
}

.markdown-body .pl-v,
.markdown-body .pl-smw {
  color: #953800;
}

.markdown-body .pl-bu {
  color: #82071e;
}

.markdown-body .pl-ii {
  color: #f6f8fa;
  background-color: #82071e;
}

.markdown-body .pl-c2 {
  color: #f6f8fa;
  background-color: #cf222e;
}

.markdown-body .pl-sr .pl-cce {
  font-weight: bold;
  color: #116329;
}

.markdown-body .pl-ml {
  color: #3b2300;
}

.markdown-body .pl-mh,
.markdown-body .pl-mh .pl-en,
.markdown-body .pl-ms {
  font-weight: bold;
  color: #0550ae;
}

.markdown-body .pl-mi {
  font-style: italic;
  color: #1f2328;
}

.markdown-body .pl-mb {
  font-weight: bold;
  color: #1f2328;
}

.markdown-body .pl-md {
  color: #82071e;
  background-color: #ffebe9;
}

.markdown-body .pl-mi1 {
  color: #116329;
  background-color: #dafbe1;
}

.markdown-body .pl-mc {
  color: #953800;
  background-color: #ffd8b5;
}

.markdown-body .pl-mi2 {
  color: #d1d9e0;
  background-color: #0550ae;
}

.markdown-body .pl-mdr {
  font-weight: bold;
  color: #8250df;
}

.markdown-body .pl-ba {
  color: #59636e;
}

.markdown-body .pl-sg {
  color: #818b98;
}

.markdown-body .pl-corl {
  text-decoration: underline;
  color: #0a3069;
}

.markdown-body [role='button']:focus:not(:focus-visible),
.markdown-body [role='tabpanel'][tabindex='0']:focus:not(:focus-visible),
.markdown-body button:focus:not(:focus-visible),
.markdown-body summary:focus:not(:focus-visible),
.markdown-body a:focus:not(:focus-visible) {
  outline: none;
  box-shadow: none;
}

.markdown-body [tabindex='0']:focus:not(:focus-visible),
.markdown-body details-dialog:focus:not(:focus-visible) {
  outline: none;
}

.markdown-body g-emoji {
  display: inline-block;
  min-width: 1ch;
  font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  font-size: 1em;
  font-style: normal !important;
  font-weight: 400;
  line-height: 1;
  vertical-align: -0.075em;
}

.markdown-body g-emoji img {
  width: 1em;
  height: 1em;
}

.markdown-body .task-list-item {
  list-style-type: none;
}

.markdown-body .task-list-item label {
  font-weight: 400;
}

.markdown-body .task-list-item.enabled label {
  cursor: pointer;
}

.markdown-body .task-list-item + .task-list-item {
  margin-top: 0.25rem;
}

.markdown-body .task-list-item .handle {
  display: none;
}

.markdown-body .task-list-item-checkbox {
  margin: 0 0.2em 0.25em -1.4em;
  vertical-align: middle;
}

.markdown-body ul:dir(rtl) .task-list-item-checkbox {
  margin: 0 -1.6em 0.25em 0.2em;
}

.markdown-body ol:dir(rtl) .task-list-item-checkbox {
  margin: 0 -1.6em 0.25em 0.2em;
}

.markdown-body .contains-task-list:hover .task-list-item-convert-container,
.markdown-body .contains-task-list:focus-within .task-list-item-convert-container {
  display: block;
  width: auto;
  height: 24px;
  overflow: visible;
  clip: auto;
}

.markdown-body ::-webkit-calendar-picker-indicator {
  filter: invert(50%);
}

.markdown-body .markdown-alert {
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  color: inherit;
  border-left: 0.25em solid #d1d9e0;
}

.markdown-body .markdown-alert > :first-child {
  margin-top: 0;
}

.markdown-body .markdown-alert > :last-child {
  margin-bottom: 0;
}

.markdown-body .markdown-alert .markdown-alert-title {
  display: flex;
  font-weight: 500;
  align-items: center;
  line-height: 1;
}

.markdown-body .markdown-alert.markdown-alert-note {
  border-left-color: #0969da;
}

.markdown-body .markdown-alert.markdown-alert-note .markdown-alert-title {
  color: #0969da;
}

.markdown-body .markdown-alert.markdown-alert-important {
  border-left-color: #8250df;
}

.markdown-body .markdown-alert.markdown-alert-important .markdown-alert-title {
  color: #8250df;
}

.markdown-body .markdown-alert.markdown-alert-warning {
  border-left-color: #9a6700;
}

.markdown-body .markdown-alert.markdown-alert-warning .markdown-alert-title {
  color: #9a6700;
}

.markdown-body .markdown-alert.markdown-alert-tip {
  border-left-color: #1a7f37;
}

.markdown-body .markdown-alert.markdown-alert-tip .markdown-alert-title {
  color: #1a7f37;
}

.markdown-body .markdown-alert.markdown-alert-caution {
  border-left-color: #cf222e;
}

.markdown-body .markdown-alert.markdown-alert-caution .markdown-alert-title {
  color: #d1242f;
}

.markdown-body > *:first-child > .heading-element:first-child {
  margin-top: 0 !important;
}

.markdown-body .highlight pre:has(+ .zeroclipboard-container) {
  min-height: 52px;
}

/* ---------------------------------------------------------------------------
 * MYAC-1151 — `markdown-compact`, and why it is a modifier rather than an edit.
 *
 * Everything above is github-markdown-css: margins sized for a README on a wide
 * page. The chat panel renders it at ~420px, where they cost far more than they
 * are worth — a structured answer runs about a third longer than it needs to,
 * and less of the conversation fits on screen.
 *
 * TWO THINGS ABOUT THIS BLOCK ARE LOAD-BEARING.
 *
 * 1. It must stay LAST in the file. Every selector here is deliberately the
 *    same specificity as the `.markdown-body` rule it overrides — `.markdown-
 *    compact ul` (0,1,1) against `.markdown-body ul` (0,1,1) — so order is what
 *    decides. Appending `.markdown-body` rules below this block would silently
 *    win.
 *
 *    The obvious hardening — writing `.markdown-body.markdown-compact ul`
 *    instead — is WRONG here, and quietly so. Two classes and a type is
 *    (0,2,1), and the class column decides before the type column ever gets
 *    read: 2 beats the 1 in `.markdown-body ul ol` (0,1,2), the ladder that
 *    zeroes margins on NESTED lists. Every nested list would gain a bottom
 *    margin it is meant not to have. Matching specificity is the point, not an
 *    oversight.
 *
 * 2. Nothing here touches `list-style-type`. The marker ladder further up (see
 *    the long comment at `.markdown-body ul`) composes by specificity alone; a
 *    rule from this block landing in it would put bullets back on nested
 *    ordered lists — app-wide, on every chat answer.
 *
 * Body line-height stops at 1.5. That is the comfortable floor for running
 * text, and going below it to buy a few more pixels is how "dense" becomes
 * "cramped".
 * ------------------------------------------------------------------------- */
.markdown-compact {
  line-height: 1.5;
}

/* Headings flatten. In a narrow column size stops being a useful hierarchy
 * signal — weight, colour and the space above carry it instead, which is what
 * the `front-ui` skill asks for ("large headings are rare; prefer subtle weight/size
 * steps"). The rule under h1/h2 goes with the size: a full-width divider under
 * a two-word heading is how a chat reply ends up looking like a document.
 *
 * A real step survives between h1, h2 and h3, and h6 keeps its muted colour
 * from the base stylesheet. Collapsing all six to one size would trade a
 * whitespace problem for a hierarchy problem. */
.markdown-compact h1,
.markdown-compact h2,
.markdown-compact h3,
.markdown-compact h4,
.markdown-compact h5,
.markdown-compact h6 {
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

.markdown-compact h1 {
  font-size: 1.15em;
  padding-bottom: 0;
  border-bottom: 0;
}

.markdown-compact h2 {
  font-size: 1.05em;
  padding-bottom: 0;
  border-bottom: 0;
}

.markdown-compact h3,
.markdown-compact h4,
.markdown-compact h5,
.markdown-compact h6 {
  font-size: 1em;
}

.markdown-compact p,
.markdown-compact blockquote,
.markdown-compact ul,
.markdown-compact ol,
.markdown-compact dl,
.markdown-compact table,
.markdown-compact pre,
.markdown-compact details {
  margin-bottom: 0.5rem;
}

/* 2em of indent out of a 420px panel is 7% of the line, spent on a bullet. */
.markdown-compact ul,
.markdown-compact ol {
  padding-left: 1.25em;
}

/* A LOOSE list — blank lines between items, so the parser wraps each item's
 * content in a `<p>` — should not render differently from a tight one. Nobody
 * chose that; CommonMark did. `li + li` carries the separation instead, and
 * `p + p` keeps a genuinely multi-paragraph item readable. */
.markdown-compact li > p {
  margin-top: 0;
  margin-bottom: 0;
}

.markdown-compact li > p + p {
  margin-top: 0.5rem;
}

.markdown-compact li + li {
  margin-top: 0.125rem;
}

/* A rule, not a bar. */
.markdown-compact hr {
  height: 1px;
  margin: 0.75rem 0;
}
</style>
